import { YellowButton } from '../../styles/buttons';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { getSignUpLink } from '~services/business/pricing/get-signup-link';
import { mobileBreakpoint, smallScreenBreakpoint1064px } from '~styles/common';
import { PricingPlanPrice } from './components/pricing-plan-price';
import { FeatureList } from './components/feature-list';
import { Mode, Tier } from '~services/business/pricing';
import { paths } from 'shared/routing';
import { Membership } from '~/components/pricing/types';
import { Country } from '~services/i18n/country';
import { Currency } from '~services/i18n/currency';

type PricingPlanProps = {
    bg?: string;
    isBulletPlus?: boolean;
    country: Country;
    currency: Currency;
    hasDivider?: boolean;
    language: string;
    membership: Membership;
    mode: Mode;
    hasSavings?: boolean;
    isYearly?: boolean;
};

export const PricingPlan = ({
    bg,
    isBulletPlus,
    country,
    currency,
    hasDivider,
    language,
    membership,
    mode,
    hasSavings,
    isYearly,
}: PricingPlanProps) => {
    const {
        heading,
        headingImg,
        headingAllFrom,
        features,
        paymentsUpTo,
        tier,
    } = membership;

    return (
        <Card bg={bg}>
            <CardHeading>
                {headingImg && (
                    <ImageHeading src={headingImg} alt="Price plan image" />
                )}
                <Trans id={heading} />
            </CardHeading>

            <PricingPlanPrice
                currency={currency}
                hasSavings={hasSavings}
                isYearly={isYearly}
                language={language}
                mode={mode}
                tier={tier}
            />

            {tier === Tier.PREMIUM ? (
                <CTAButton href={paths.pos_sign_up.cz}>
                    <Trans id="global.pricing.signUp" />
                </CTAButton>
            ) : (
                <CTAButton href={getSignUpLink(country, tier, mode)}>
                    <Trans id="global.pricing.signUp" />
                </CTAButton>
            )}

            {headingAllFrom && (
                <FreeFeature>
                    <Trans id="global.pricing.features.premium.allFrom" />{' '}
                    <FreePlan>
                        <Trans id={headingAllFrom} />
                    </FreePlan>
                </FreeFeature>
            )}

            <FeatureList
                hasDivider={hasDivider}
                paymentsUpTo={paymentsUpTo}
                features={features}
                isBulletPlus={isBulletPlus}
                currency={currency}
                language={language}
                tier={tier}
            />
        </Card>
    );
};

type PropsCard = {
    bg?: string;
};

const CTAButton = styled(YellowButton)`
    margin: 15px 0;
    max-width: max-content;
`;

const Card = styled.div<PropsCard>`
    background: ${props => (props.bg ? props.bg : 'none')};
    padding: 29px;
    border: ${props => !props.bg && '0.5px solid #676aa7'};
    border-radius: 20px;
    display: flex;
    flex-flow: column;
    min-width: 330px;
    max-width: 360px;
    min-height: 540px;
    flex: 1;
    @media (max-width: ${smallScreenBreakpoint1064px}) {
        width: 380px;
    }
    @media (max-width: ${mobileBreakpoint}) {
        width: 330px;
    }
`;

const CardHeading = styled.div`
    font-style: normal;
    font-weight: 700;
    font-size: 30px;
    line-height: 45px;
    margin-bottom: 40px;
    display: flex;
    align-items: center;
`;

const FreePlan = styled.span`
    font-weight: 700;
    color: ${({ theme }) => theme.greenColor};
`;

const FreeFeature = styled.div`
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 29px;
    letter-spacing: -0.25px;
`;

const ImageHeading = styled.img`
    margin-right: 0.6rem;
`;
